import React from 'react';
import {
  getDefaultSiteContent,
  loadSiteContent,
  saveSiteContent,
  resetSiteContentStorage,
  deepMerge,
  setByPath,
  SITE_CONTENT_STORAGE_KEY
} from './siteContentUtils';
import { fetchRemoteSiteContent, fetchPublicSiteContent, pushRemoteSiteContent } from '../services/siteContentApi';

const SiteContentContext = React.createContext({
  content: getDefaultSiteContent(),
  setContent: () => {},
  setContentAtPath: () => {},
  replaceContent: () => {},
  resetContent: () => {},
  remoteStorageMode: 'memory',
});

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = React.useState(() => loadSiteContent());
  const [isRemoteInitDone, setIsRemoteInitDone] = React.useState(false);
  const [remoteStorageMode, setRemoteStorageMode] = React.useState('memory');

  React.useEffect(() => {
    saveSiteContent(content);
  }, [content]);

  React.useEffect(() => {
    let isMounted = true;

    const initializeFromRemote = async () => {
      let remotePayload = null;
      let publicPayload = null;

      try {
        remotePayload = await fetchRemoteSiteContent();
      } catch {
        remotePayload = null;
      }

      if (isMounted) {
        setRemoteStorageMode(remotePayload?.storageMode === 'blob' ? 'blob' : 'memory');
      }

      try {
        publicPayload = await fetchPublicSiteContent();
      } catch {
        publicPayload = null;
      }

      if (!isMounted) {
        return;
      }

      const remoteContent = remotePayload?.content;
      const shouldUseRemote = remotePayload?.storageMode === 'blob' && remoteContent && typeof remoteContent === 'object';
      const preferredContent = shouldUseRemote ? remoteContent : publicPayload;

      if (preferredContent && typeof preferredContent === 'object') {
        setContent(deepMerge(getDefaultSiteContent(), preferredContent));
        return;
      }

      if (remoteContent && typeof remoteContent === 'object') {
        setContent(deepMerge(getDefaultSiteContent(), remoteContent));
        return;
      }

      if (publicPayload && typeof publicPayload === 'object') {
        setContent(deepMerge(getDefaultSiteContent(), publicPayload));
        return;
      }
    };

    initializeFromRemote()
      .catch(() => {
        // No remote content yet or API unavailable; keep local content.
      })
      .finally(() => {
        if (isMounted) {
          setIsRemoteInitDone(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (!isRemoteInitDone || remoteStorageMode !== 'blob') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      pushRemoteSiteContent(content).catch(() => {
        // Silent fallback: local storage remains the source when API is unavailable.
      });
    }, 600);

    return () => window.clearTimeout(timeoutId);
  }, [content, isRemoteInitDone, remoteStorageMode]);

  React.useEffect(() => {
    const handleStorage = (event) => {
      if (event.key && event.key !== SITE_CONTENT_STORAGE_KEY) {
        return;
      }

      setContent(loadSiteContent());
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const setContentAtPath = (path, value) => {
    setContent((prev) => setByPath(prev, path, value));
  };

  const replaceContent = (nextContent) => {
    setContent(deepMerge(getDefaultSiteContent(), nextContent || {}));
  };

  const resetContent = () => {
    resetSiteContentStorage();
    setContent(getDefaultSiteContent());
  };

  return (
    <SiteContentContext.Provider
      value={{
        content,
        setContent,
        setContentAtPath,
        replaceContent,
        resetContent,
        remoteStorageMode,
      }}
    >
      {children}
    </SiteContentContext.Provider>
  );
};

export default SiteContentContext;
